import { useEffect, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { cursorArraste } from './cursor';

/*
  Arraste com ponteiro, no lugar do arraste nativo do navegador (HTML5 drag and drop).

  No arraste nativo o navegador desenha a seta do sistema e o CSS não troca o cursor; aqui a
  manopla fechada aparece o tempo todo (src/input/cursor.ts). A carga imita o DataTransfer:
  um mapa de tipo → texto, então quem lia `dataTransfer.getData(DRAG_ITEM)` passa a ler
  `carga[DRAG_ITEM]`.

  Origem: `prepararArraste(e, carga)` no pointerdown. Só vira arraste depois de LIMIAR px; até
  lá é clique comum. Depois de um arraste, o clique que o navegador dispara no fim é engolido.
  Destino: `useAlvoDeArraste(ref, alvo)` num elemento. Vale o alvo mais de dentro que aceitar
  (`sobre` devolve true), como o dragover com preventDefault. Esc cancela.

  Só mouse e caneta: no toque, o jogo usa tocar no item e os botões da ficha.
*/

export type Carga = Readonly<Record<string, string>>;

export interface Soltura {
  readonly carga: Carga;
  /** O elemento debaixo do ponteiro (o `e.target` do drop nativo). */
  readonly alvo: Element;
  readonly x: number;
  readonly y: number;
}

export interface AlvoDeArraste {
  /** Aceita a carga sobre este ponto? Sem `sobre`, aceita tudo. */
  sobre?(s: Soltura): boolean;
  solta(s: Soltura): void;
  /** O ponteiro saiu (ou o arraste acabou) depois de um `sobre` aceito. */
  sai?(): void;
}

export interface OpcoesDoArraste {
  /** O que vai na mão. Sem isto, o primeiro `.itemicon` (ou `img`) da origem. */
  fantasma?: Element | null;
  onInicio?(): void;
  onFim?(soltou: boolean): void;
}

const LIMIAR = 5;
const alvos = new WeakMap<Element, Set<{ current: AlvoDeArraste }>>();


/** Registra um alvo num elemento. Devolve quem desfaz. */
export function registrarAlvo(el: Element, alvo: { current: AlvoDeArraste }): () => void {
  let lista = alvos.get(el);
  if (!lista) {
    lista = new Set();
    alvos.set(el, lista);
  }
  lista.add(alvo);
  return () => { lista.delete(alvo); };
}

/** O alvo lê sempre a versão mais nova das funções (estado do React incluso). */
export function useAlvoDeArraste(ref: RefObject<Element | null>, alvo: AlvoDeArraste, ativo = true): void {
  const atual = useRef(alvo);
  atual.current = alvo;
  // Sem lista de dependências: o elemento pode aparecer depois (render condicional).
  // Registrar de novo a cada render é só um add/delete num Set.
  useEffect(() => {
    const el = ref.current;
    if (!el || !ativo) return;
    return registrarAlvo(el, atual);
  });
}

/** Alvo no documento inteiro: o que cai fora de tudo (ex.: jogar o item no chão). */
export function useAlvoNoDocumento(alvo: AlvoDeArraste, ativo = true): void {
  const atual = useRef(alvo);
  atual.current = alvo;
  useEffect(() => {
    if (!ativo) return;
    return registrarAlvo(document.documentElement, atual);
  }, [ativo]);
}

type Caixa = { current: AlvoDeArraste };

/** Devolve a caixa (não o objeto): o objeto muda a cada render do React, a caixa não. */
function acharAlvo(s: Omit<Soltura, 'alvo'>, el: Element | null): { caixa: Caixa; soltura: Soltura } | null {
  if (!el) return null;
  const soltura: Soltura = { ...s, alvo: el };
  for (let no: Element | null = el; no; no = no.parentElement) {
    const lista = alvos.get(no);
    if (!lista) continue;
    for (const caixa of lista) {
      const a = caixa.current;
      if (!a.sobre || a.sobre(soltura)) return { caixa, soltura };
    }
  }
  return null;
}

function fantasmaDe(origem: Element, opts: OpcoesDoArraste): HTMLElement | null {
  const base = opts.fantasma ?? origem.querySelector('.itemicon') ?? origem.querySelector('img');
  if (!base) return null;
  const caixa = base.getBoundingClientRect();
  const copia = base.cloneNode(true) as HTMLElement;
  copia.classList.add('arraste-fantasma');
  copia.removeAttribute('id');
  copia.setAttribute('aria-hidden', 'true');
  copia.style.width = `${caixa.width}px`;
  copia.style.height = `${caixa.height}px`;
  document.body.appendChild(copia);
  return copia;
}

function engolirClique() {
  const engole = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  window.addEventListener('click', engole, true);
  window.setTimeout(() => window.removeEventListener('click', engole, true), 0);
}

let emCurso: (() => void) | null = null;
let arrastando = false;

/** Há um item na mão agora? (o Esc do atalhos.ts deixa o arraste cancelar em vez de fechar janela) */
export function arrastandoItem(): boolean {
  return arrastando;
}

/**
 * Começa a acompanhar um possível arraste a partir de um pointerdown.
 * `carga` pode ser função: é lida quando o arraste começa de fato.
 */
export function prepararArraste(
  e: ReactPointerEvent<Element> | PointerEvent,
  carga: Carga | (() => Carga),
  opts: OpcoesDoArraste = {}
): void {
  // A janela (GumpWindow) não pode começar a se mover junto, nem no toque.
  e.stopPropagation();
  if (e.button !== 0 || e.pointerType === 'touch') return;
  emCurso?.();
  const origem = e.currentTarget as Element;
  const x0 = e.clientX;
  const y0 = e.clientY;
  const id = e.pointerId;
  let ativo = false;
  let dados: Carga = {};
  let fantasma: HTMLElement | null = null;
  let meiaL = 0;
  let meiaA = 0;
  let atual: Caixa | null = null;
  let selecao = '';

  const posicionar = (x: number, y: number) => {
    if (fantasma) fantasma.style.transform = `translate(${Math.round(x - meiaL)}px, ${Math.round(y - meiaA)}px)`;
  };

  const procurar = (x: number, y: number) => {
    const achado = acharAlvo({ carga: dados, x, y }, document.elementFromPoint(x, y));
    const novo = achado?.caixa ?? null;
    if (atual && atual !== novo) atual.current.sai?.();
    atual = novo;
    return achado;
  };

  const comecar = () => {
    ativo = true;
    arrastando = true;
    dados = typeof carga === 'function' ? carga() : carga;
    fantasma = fantasmaDe(origem, opts);
    if (fantasma) {
      const r = fantasma.getBoundingClientRect();
      // O item fica "na mão": um pouco à direita e abaixo da ponta da manopla.
      meiaL = r.width * 0.3;
      meiaA = r.height * 0.3;
    }
    selecao = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    window.getSelection()?.removeAllRanges();
    origem.classList.add('arraste-origem');
    cursorArraste('item', true);
    opts.onInicio?.();
  };

  const terminar = (soltar: boolean, x: number, y: number) => {
    window.removeEventListener('pointermove', mover, true);
    window.removeEventListener('pointerup', subir, true);
    window.removeEventListener('pointercancel', cancelar, true);
    window.removeEventListener('keydown', tecla, true);
    window.removeEventListener('blur', cancelarSemPonto);
    emCurso = null;
    if (!ativo) return;
    arrastando = false;
    let soltou = false;
    if (soltar) {
      const achado = procurar(x, y);
      if (achado) {
        soltou = true;
        try {
          achado.caixa.current.solta(achado.soltura);
        } finally {
          achado.caixa.current.sai?.();
        }
      }
    } else {
      atual?.current.sai?.();
    }
    atual = null;
    fantasma?.remove();
    fantasma = null;
    origem.classList.remove('arraste-origem');
    document.body.style.userSelect = selecao;
    cursorArraste('item', false);
    engolirClique();
    opts.onFim?.(soltou);
  };

  const mover = (ev: PointerEvent) => {
    if (ev.pointerId !== id) return;
    if (ev.buttons === 0) {
      // Soltou fora da janela do navegador: não chegou pointerup.
      terminar(false, ev.clientX, ev.clientY);
      return;
    }
    if (!ativo) {
      if (Math.hypot(ev.clientX - x0, ev.clientY - y0) < LIMIAR) return;
      comecar();
    }
    ev.preventDefault();
    posicionar(ev.clientX, ev.clientY);
    procurar(ev.clientX, ev.clientY);
  };
  const subir = (ev: PointerEvent) => {
    if (ev.pointerId === id) terminar(true, ev.clientX, ev.clientY);
  };
  const cancelar = (ev: PointerEvent) => {
    if (ev.pointerId === id) terminar(false, ev.clientX, ev.clientY);
  };
  const tecla = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape' || !ativo) return;
    ev.preventDefault();
    ev.stopPropagation();
    terminar(false, 0, 0);
  };
  const cancelarSemPonto = () => terminar(false, 0, 0);

  window.addEventListener('pointermove', mover, true);
  window.addEventListener('pointerup', subir, true);
  window.addEventListener('pointercancel', cancelar, true);
  window.addEventListener('keydown', tecla, true);
  window.addEventListener('blur', cancelarSemPonto);
  emCurso = cancelarSemPonto;
}

/** Lê a carga como se fosse um DataTransfer (para quem já tinha `lerCorpseSlot(dt, …)`). */
export function comoDataTransfer(carga: Carga): Pick<DataTransfer, 'getData'> {
  return { getData: (tipo: string) => carga[tipo] ?? '' };
}
